import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, Bell, BellOff, UtensilsCrossed, CheckCircle, Lock } from 'lucide-react';
import { collection, query, where, onSnapshot, doc, getDoc,
  updateDoc, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import app, { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const PIN_KEY = 'staff_pin_';

export default function Staff() {
  const { storeId } = useParams();
  const navigate = useNavigate();

  const [storeUid, setStoreUid] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const [calls, setCalls] = useState([]);
  const [menus, setMenus] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paidOrders, setPaidOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeTab, setActiveTab] = useState('calls');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [manualOrderMode, setManualOrderMode] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [orderingCall, setOrderingCall] = useState(null);
  const [orderItems, setOrderItems] = useState({});

  const audioCtx = useRef(null);
  const prevCallIds = useRef(new Set());
  const [undoOrders, setUndoOrders] = useState(null);
  const undoTimerRef = useRef(null);
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('staffLang') || 'ja'; } catch (e) { return 'ja'; }
  });

  useEffect(() => {
    try { localStorage.setItem('staffLang', lang); } catch (e) {}
  }, [lang]);

  const dtLocale = lang === 'en' ? 'en-US' : 'ja-JP';

  const menuName = (menu) => (lang === 'en' && menu && menu.nameEn) ? menu.nameEn : (menu ? menu.nameJa : '');

  const itemName = (item) => {
    if (lang === 'en') {
      if (item.nameEn) return item.nameEn;
      const menu = menus.find(m => m.id === item.menuId);
      if (menu && menu.nameEn) return menu.nameEn;
    }
    return item.nameJa;
  };

  const T = {
    ja: {
      storeNotFound: '店舗が見つかりません',
      storeInfoNotFound: '店舗情報が見つかりません',
      loadError: '読み込みエラー: ',
      pinWrong: 'PINが間違っています',
      pinCheckError: 'PIN確認中にエラーが発生しました',
      selectMenuAlert: 'メニューを選択してください',
      orderSaveError: '注文の保存に失敗しました',
      thisOrder: 'この注文',
      loading: '読み込み中...',
      proOnlyTitle: 'Proプランの機能です',
      proOnlyDesc: 'スタッフ画面はProプラン（¥980/月）でご利用いただけます',
      backToMenu: 'メニューに戻る',
      staffOnly: 'スタッフ専用画面',
      verifying: '確認中...',
      orderInputFor: (name) => `${name} の注文入力`,
      cancel: 'キャンセル',
      total: '合計',
      confirmOrder: '注文確定',
      staffBadge: 'スタッフ',
      logout: 'ログアウト',
      paidBanner: (n) => `${n}件を会計済みにしました`,
      undo: '元に戻す',
      tabCalls: '呼び出し',
      tabOrders: '注文一覧',
      tabPaid: '会計済み',
      tabCounter: 'カウント',
      manualOrderBtn: '＋ 手動で注文入力',
      selectTable: 'テーブルを選択',
      orderInputArrow: '注文入力へ →',
      noCalls: '呼び出しはありません',
      orderInputBtn: '注文入力',
      confirmBtn: '確認',
      doneBtn: '完了',
      noOrders: '注文はありません',
      notePlaceholder: 'メモ（例：2名様・お子様連れ・アレルギー等）',
      confirmCancelOrder: 'この注文だけキャンセルしますか？',
      cancelThisOrder: 'この注文をキャンセル',
      payThisOrder: 'この注文を会計',
      cooked: '調理完了',
      back: '戻す',
      served: '提供済み',
      taxIncluded: '（税込）',
      confirmCancelTable: 'この注文をキャンセルしますか？',
      payAndClear: '会計済み・クリア',
      paidTabDesc: '本日会計済みの注文です。間違えた場合は「未会計に戻す」で注文一覧に戻せます。',
      noPaidToday: '本日の会計済み注文はありません',
      paidAtLabel: '会計 ',
      revertToOpen: '未会計に戻す',
      counterDesc: '＋1 で本日の販売数をカウント。在庫切れは売切ボタンで切り替えます。',
      soldToday: (n) => `・ 本日 ${n} 食`,
      soldOutActive: '売切中',
      soldOutBtn: '売切',
      thisOrderAmount: (amt) => `（この注文 ¥${amt}）`,
      totalWithAmount: (amt) => `合計 ¥${amt}`,
      confirmPay: (label, total) => `${label}（合計¥${total}）を会計済みにしますか？`,
      confirmRevert: (tableName, amt) => `${tableName}の注文（¥${amt}）を未会計に戻しますか？`,
    },
    en: {
      storeNotFound: 'Store not found',
      storeInfoNotFound: 'Store information not found',
      loadError: 'Loading error: ',
      pinWrong: 'Incorrect PIN',
      pinCheckError: 'An error occurred while checking the PIN',
      selectMenuAlert: 'Please select a menu item',
      orderSaveError: 'Failed to save the order',
      thisOrder: 'this order',
      loading: 'Loading...',
      proOnlyTitle: 'Pro plan feature',
      proOnlyDesc: 'The staff screen is available on the Pro plan (¥980/month)',
      backToMenu: 'Back to menu',
      staffOnly: 'Staff only',
      verifying: 'Verifying...',
      orderInputFor: (name) => `Order entry — ${name}`,
      cancel: 'Cancel',
      total: 'Total',
      confirmOrder: 'Confirm order',
      staffBadge: 'Staff',
      logout: 'Log out',
      paidBanner: (n) => `Marked ${n} order${n > 1 ? 's' : ''} as paid`,
      undo: 'Undo',
      tabCalls: 'Calls',
      tabOrders: 'Orders',
      tabPaid: 'Paid',
      tabCounter: 'Count',
      manualOrderBtn: '+ Manual order entry',
      selectTable: 'Select a table',
      orderInputArrow: 'To order entry →',
      noCalls: 'No calls',
      orderInputBtn: 'Order entry',
      confirmBtn: 'Confirm',
      doneBtn: 'Done',
      noOrders: 'No orders',
      notePlaceholder: 'Note (e.g. party of 2, with kids, allergies)',
      confirmCancelOrder: 'Cancel just this order?',
      cancelThisOrder: 'Cancel this order',
      payThisOrder: 'Pay this order',
      cooked: 'Ready',
      back: 'Back',
      served: 'Served',
      taxIncluded: '(tax incl.)',
      confirmCancelTable: 'Cancel this order?',
      payAndClear: 'Mark paid & clear',
      paidTabDesc: 'Orders paid today. If you made a mistake, use "Revert to unpaid" to move it back to Orders.',
      noPaidToday: 'No orders paid today',
      paidAtLabel: 'Paid ',
      revertToOpen: 'Revert to unpaid',
      counterDesc: 'Tap +1 to count today\'s sales. Use the sold-out button to mark items unavailable.',
      soldToday: (n) => `・ ${n} sold today`,
      soldOutActive: 'Sold out',
      soldOutBtn: 'Sold out',
      thisOrderAmount: (amt) => `(this order ¥${amt})`,
      totalWithAmount: (amt) => `Total ¥${amt}`,
      confirmPay: (label, total) => `Mark ${label} (total ¥${total}) as paid?`,
      confirmRevert: (tableName, amt) => `Revert ${tableName}'s order (¥${amt}) to unpaid?`,
    },
  }[lang];

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const idDoc = await getDoc(doc(db, 'storeIds', storeId));
        if (!idDoc.exists()) { setError(T.storeNotFound); setLoading(false); return; }
        const uid = idDoc.data().uid;
        setStoreUid(uid);
        const storeDoc = await getDoc(doc(db, 'stores', uid));
        if (!storeDoc.exists()) { setError(T.storeInfoNotFound); setLoading(false); return; }
        const data = storeDoc.data();
        setStoreData(data);
        setPlan(data.plan || 'free');
        const sessionAuth = sessionStorage.getItem(PIN_KEY + storeId);
        if (sessionAuth === 'ok') setAuthenticated(true);
        setLoading(false);
      } catch (e) {
        setError(T.loadError + e.message);
        setLoading(false);
      }
    };
    fetchStore();
  }, [storeId]);

  const hasAccess = plan === 'standard' || plan === 'premium' || plan === 'pro';

  useEffect(() => {
    if (!authenticated || !storeUid || !hasAccess) return;
    const q = query(
      collection(db, 'stores', storeUid, 'calls'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const newCalls = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const isNew = newCalls.some(c => !prevCallIds.current.has(c.id));
      if (isNew && prevCallIds.current.size > 0 && soundEnabled) playBeep();
      prevCallIds.current = new Set(newCalls.map(c => c.id));
      setCalls(newCalls);
    });
    return () => unsub();
  }, [authenticated, storeUid, hasAccess, soundEnabled]);

  useEffect(() => {
    if (!authenticated || !storeUid || !hasAccess) return;
    const q = query(collection(db, 'stores', storeUid, 'tables'), orderBy('order'));
    const unsub = onSnapshot(q, (snap) => {
      setTables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [authenticated, storeUid, hasAccess]);

  useEffect(() => {
    if (!authenticated || !storeUid || !hasAccess) return;
    const q = query(collection(db, 'stores', storeUid, 'menus'), orderBy('order'));
    const unsub = onSnapshot(q, (snap) => {
      setMenus(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [authenticated, storeUid, hasAccess]);

  useEffect(() => {
    if (!authenticated || !storeUid || !hasAccess) return;
    const q = query(
      collection(db, 'stores', storeUid, 'orders'),
      where('status', '==', 'open')
    );
    const unsub = onSnapshot(q, (snap) => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [authenticated, storeUid, hasAccess]);

  useEffect(() => {
    if (!authenticated || !storeUid || !hasAccess) return;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const q = query(
      collection(db, 'stores', storeUid, 'orders'),
      where('status', '==', 'paid'),
      where('createdAt', '>=', todayStart)
    );
    const unsub = onSnapshot(q, (snap) => {
      setPaidOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [authenticated, storeUid, hasAccess]);

  const playBeep = () => {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      [0, 150, 300].forEach(delay => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay / 1000);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay / 1000 + 0.3);
        osc.start(ctx.currentTime + delay / 1000);
        osc.stop(ctx.currentTime + delay / 1000 + 0.3);
      });
    } catch (e) {}
  };

  const handlePinInput = (key) => {
    if (key === 'del') { setPin(p => p.slice(0, -1)); return; }
    const next = pin + key;
    setPin(next);
    if (next.length === 4) {
      setVerifying(true);
      const functions = getFunctions(app, 'us-central1');
      const verifyStaffPin = httpsCallable(functions, 'verifyStaffPin');
      verifyStaffPin({ storeId, pin: next })
        .then((result) => {
          setVerifying(false);
          if (result.data && result.data.success) {
            sessionStorage.setItem(PIN_KEY + storeId, 'ok');
            setAuthenticated(true);
            setPinError('');
          } else {
            setPinError(T.pinWrong);
            setPin('');
          }
        })
        .catch((e) => {
          setVerifying(false);
          setPinError(e.message || T.pinCheckError);
          setPin('');
        });
    }
  };

  const handleConfirm = async (callId) => {
    await updateDoc(doc(db, 'stores', storeUid, 'calls', callId), { status: 'confirmed' });
  };

  const handleDone = async (callId) => {
    await updateDoc(doc(db, 'stores', storeUid, 'calls', callId), { status: 'done' });
  };

  const handleSoldCount = async (menu, delta = 1) => {
    const fresh = menus.find(m => m.id === menu.id);
    if (!fresh) return;
    const next = Math.max(0, (fresh.soldCount || 0) + delta);
    await updateDoc(doc(db, 'stores', storeUid, 'menus', menu.id), { soldCount: next });
  };

  const handleToggleStatus = async (menu) => {
    const fresh = menus.find(m => m.id === menu.id);
    if (!fresh) return;
    const newStatus = fresh.status === 'soldout' ? 'available' : 'soldout';
    await updateDoc(doc(db, 'stores', storeUid, 'menus', menu.id), { status: newStatus });
  };

  const handleOrderQuantity = (menuId, delta) => {
    setOrderItems(prev => {
      const current = prev[menuId] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [menuId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [menuId]: next };
    });
  };

  const handleOrderSubmit = async () => {
    if (!orderingCall && !selectedTable) return;
    const items = Object.entries(orderItems).map(([menuId, quantity]) => {
      const menu = menus.find(m => m.id === menuId);
      return { menuId, nameJa: menu.nameJa, nameEn: menu.nameEn || '', price: menu.price, quantity, status: 'cooking' };
    });
    if (items.length === 0) { alert(T.selectMenuAlert); return; }
    const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    try {
      await addDoc(collection(db, 'stores', storeUid, 'orders'), {
        tableId: orderingCall ? orderingCall.tableId || '' : selectedTable.id,
        tableName: orderingCall ? orderingCall.tableName : selectedTable.name,
        items,
        totalPrice,
        status: 'open',
        createdAt: serverTimestamp(),
      });
      await Promise.all(items.map(item =>
        updateDoc(doc(db, 'stores', storeUid, 'menus', item.menuId), {
          soldCount: (menus.find(m => m.id === item.menuId)?.soldCount || 0) + item.quantity
        })
      ));
      if (orderingCall) await handleConfirm(orderingCall.id);
      setOrderingCall(null);
      setSelectedTable(null);
      setManualOrderMode(false);
      setOrderItems({});
      setActiveTab('orders');
    } catch (e) {
      console.error('注文保存エラー:', e);
      alert(T.orderSaveError);
    }
  };

  const handleItemStatus = async (orderId, itemIndex, newStatus, order) => {
    const updatedItems = order.items.map((item, i) => {
      if (i !== itemIndex) return item;
      const next = { ...item, status: newStatus };
      if (newStatus === 'cooking') next.readyCount = 0;
      return next;
    });
    await updateDoc(doc(db, 'stores', storeUid, 'orders', orderId), { items: updatedItems });
  };

  const handleItemProgress = async (orderId, itemIndex, order) => {
    const item = order.items[itemIndex];
    const quantity = item.quantity || 1;
    const nextCount = (item.readyCount || 0) + 1;
    const updatedItems = order.items.map((it, i) => {
      if (i !== itemIndex) return it;
      if (nextCount >= quantity) return { ...it, status: 'ready', readyCount: quantity };
      return { ...it, readyCount: nextCount };
    });
    await updateDoc(doc(db, 'stores', storeUid, 'orders', orderId), { items: updatedItems });
  };

  const handleSaveTableNote = async (table, note) => {
    await Promise.all(table.orders.map(o =>
      updateDoc(doc(db, 'stores', storeUid, 'orders', o.id), { note })
    ));
  };

  const handleOrderPaid = async (orderId) => {
    await updateDoc(doc(db, 'stores', storeUid, 'orders', orderId), { status: 'paid', paidAt: serverTimestamp() });
  };

  const handlePayOrders = async (ordersToPay) => {
    const total = ordersToPay.reduce((sum, o) => sum + (o.totalPrice || 0), 0);
    const label = ordersToPay.length > 1 ? `${ordersToPay.length}件` : T.thisOrder;
    if (!window.confirm(T.confirmPay(label, total.toLocaleString()))) return;
    await Promise.all(ordersToPay.map(o => handleOrderPaid(o.id)));
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoOrders(ordersToPay);
    undoTimerRef.current = setTimeout(() => setUndoOrders(null), 8000);
  };

  const handleUndoPayment = async () => {
    if (!undoOrders) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    const toRestore = undoOrders;
    setUndoOrders(null);
    await Promise.all(toRestore.map(o =>
      updateDoc(doc(db, 'stores', storeUid, 'orders', o.id), { status: 'open' })
    ));
  };

  const handleRevertToOpen = async (order) => {
    if (!window.confirm(T.confirmRevert(order.tableName, (order.totalPrice||0).toLocaleString()))) return;
    await updateDoc(doc(db, 'stores', storeUid, 'orders', order.id), { status: 'open' });
  };

  const handleOrderCancel = async (order) => {
    await updateDoc(doc(db, 'stores', storeUid, 'orders', order.id), { status: 'cancelled' });
    await Promise.all(order.items.map(item =>
      updateDoc(doc(db, 'stores', storeUid, 'menus', item.menuId), {
        soldCount: Math.max(0, (menus.find(m => m.id === item.menuId)?.soldCount || 0) - item.quantity)
      })
    ));
  };

  const handleItemCancel = async (order, itemIndex) => {
    const item = order.items[itemIndex];
    const updatedItems = order.items.filter((_, i) => i !== itemIndex);
    await updateDoc(doc(db, 'stores', storeUid, 'menus', item.menuId), {
      soldCount: Math.max(0, (menus.find(m => m.id === item.menuId)?.soldCount || 0) - item.quantity)
    });
    if (updatedItems.length === 0) {
      await updateDoc(doc(db, 'stores', storeUid, 'orders', order.id), { status: 'cancelled' });
    } else {
      const newTotal = updatedItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
      await updateDoc(doc(db, 'stores', storeUid, 'orders', order.id), {
        items: updatedItems, totalPrice: newTotal
      });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PIN_KEY + storeId);
    setAuthenticated(false);
    setPin('');
  };

  const resetOrderMode = () => {
    setOrderingCall(null);
    setSelectedTable(null);
    setManualOrderMode(false);
    setOrderItems({});
  };

  if (loading) return <div style={s.center}><p style={{ color: '#888' }}>{T.loading}</p></div>;
  if (error) return <div style={s.center}><p style={{ color: '#e53e3e' }}>{error}</p></div>;

  if (!hasAccess) return (
    <div style={s.center}>
      <div style={s.planBox}>
        <div style={{ marginBottom: 16, color: '#718096' }}><Lock size={48} /></div>
        <h2 style={{ margin: '0 0 8px', color: '#2d3748' }}>{T.proOnlyTitle}</h2>
        <p style={{ color: '#718096', margin: '0 0 20px' }}>{T.proOnlyDesc}</p>
        <button onClick={() => navigate('/' + storeId)} style={s.btnPrimary}>{T.backToMenu}</button>
      </div>
    </div>
  );

  if (!authenticated) return (
    <div style={s.center}>
      <div style={s.pinBox}>
        {storeData && storeData.logoUrl && (
          <img src={storeData.logoUrl} alt="logo" style={s.logo} />
        )}
        <h2 style={{ margin: '0 0 4px', color: '#2d3748', fontSize: 20 }}>
          {storeData ? storeData.storeName : ''}
        </h2>
        <p style={{ color: '#718096', margin: '0 0 24px', fontSize: 14 }}>{T.staffOnly}</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, justifyContent: 'center' }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{
              width: 48, height: 56, border: '2px solid',
              borderColor: pin.length > i ? '#667eea' : '#e2e8f0',
              borderRadius: 10, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 24, fontWeight: 700,
              color: '#2d3748', background: '#fff',
            }}>
              {pin.length > i ? '●' : ''}
            </div>
          ))}
        </div>
        {pinError && <p style={{ color: '#e53e3e', fontSize: 13, margin: '0 0 8px' }}>{pinError}</p>}
        {verifying && <p style={{ color: '#718096', fontSize: 13, margin: '0 0 8px' }}>{T.verifying}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 64px)', gap: 10 }}>
          {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((k, i) => (
            <button key={i} disabled={k === '' || verifying}
              onClick={() => { if (k === '⌫') handlePinInput('del'); else if (k !== '') handlePinInput(String(k)); }}
              style={{
                width: 64, height: 56, borderRadius: 10, border: '1px solid #e2e8f0',
                background: k === '' ? 'transparent' : '#f7fafc',
                fontSize: k === '⌫' ? 20 : 22, fontWeight: 500,
                color: '#2d3748', cursor: k === '' ? 'default' : 'pointer',
              }}
            >{k}</button>
          ))}
        </div>
      </div>
    </div>
  );

  if (orderingCall || (manualOrderMode && selectedTable)) return (
    <div style={{ minHeight: '100vh', background: '#f7fafc' }}>
      <div style={s.header}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>
          <UtensilsCrossed size={16} style={{display:'inline',verticalAlign:'middle',marginRight:6}} />{T.orderInputFor(orderingCall ? orderingCall.tableName : selectedTable ? selectedTable.name : '')}
        </div>
        <button onClick={resetOrderMode} style={s.logoutBtn}>{T.cancel}</button>
      </div>
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 100 }}>
          {menus.filter(m => m.status !== 'soldout').map(menu => (
            <div key={menu.id} style={s.menuCard}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#667eea' }}>No.{menu.menuNumber || '—'}</div>
                <div style={{ fontWeight: 600, color: '#2d3748' }}>{menuName(menu)}</div>
                <div style={{ fontSize: 13, color: '#718096' }}>¥{menu.price ? menu.price.toLocaleString() : 0}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => handleOrderQuantity(menu.id, -1)}
                  disabled={!orderItems[menu.id]}
                  style={{ ...s.btnCount, background: '#e2e8f0', color: '#4a5568' }}>－</button>
                <span style={{ width: 28, textAlign: 'center', fontWeight: 700, fontSize: 18 }}>
                  {orderItems[menu.id] || 0}
                </span>
                <button onClick={() => handleOrderQuantity(menu.id, 1)} style={s.btnCount}>＋</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff',
          borderTop: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#718096' }}>{T.total}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#2d3748' }}>
              ¥{Object.entries(orderItems).reduce((sum, [menuId, qty]) => {
                const menu = menus.find(m => m.id === menuId);
                return sum + (menu ? menu.price * qty : 0);
              }, 0).toLocaleString()}
            </div>
          </div>
          <button onClick={handleOrderSubmit} style={{ ...s.btnPrimary, fontSize: 16, padding: '12px 32px' }}>
            {T.confirmOrder}
          </button>
        </div>
      </div>
    </div>
  );

  const pendingCalls = calls.filter(c => c.status === 'pending');

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc' }}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1, marginRight: 12 }}>
          {storeData && storeData.logoUrl && (
            <img src={storeData.logoUrl} alt="" style={{ height: 32, borderRadius: 6, flexShrink: 0 }} />
          )}
          <span style={{ fontWeight: 600, color: '#fff', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {storeData ? storeData.storeName : ''}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button onClick={() => setSoundEnabled(v => !v)} style={s.iconBtn}>
            {soundEnabled ? <Bell size={16} /> : <BellOff size={16} />}
          </button>
          <button onClick={() => setLang(l => l === 'ja' ? 'en' : 'ja')}
            style={{ ...s.iconBtn, fontSize: 16 }} title="Language / 言語">
            {lang === 'ja' ? '🇯🇵' : '🇺🇸'}
          </button>
          <button onClick={handleLogout} style={s.logoutBtn}>{T.logout}</button>
        </div>
      </div>

      <div style={{ background: '#2d3748', color: '#fff', fontSize: 12,
        textAlign: 'center', padding: '4px 0', letterSpacing: 1 }}>
        {T.staffBadge}
      </div>

      {undoOrders && (
        <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#2d3748', color: '#fff',
          padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
          <span>{T.paidBanner(undoOrders.length)}</span>
          <button onClick={handleUndoPayment}
            style={{ background: '#fff', color: '#2d3748', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
            {T.undo}
          </button>
        </div>
      )}

      <div style={s.tabBar}>
        <button onClick={() => setActiveTab('calls')}
          style={{ ...s.tab, ...(activeTab === 'calls' ? s.tabActive : {}) }}>
          {T.tabCalls}{pendingCalls.length > 0 && <span style={s.badge}>{pendingCalls.length}</span>}
        </button>
        <button onClick={() => setActiveTab('orders')}
          style={{ ...s.tab, ...(activeTab === 'orders' ? s.tabActive : {}) }}>
          {T.tabOrders}{orders.length > 0 && <span style={s.badge}>{orders.length}</span>}
        </button>
        <button onClick={() => setActiveTab('paid')}
          style={{ ...s.tab, ...(activeTab === 'paid' ? s.tabActive : {}) }}>
          {T.tabPaid}
        </button>
        <button onClick={() => setActiveTab('counter')}
          style={{ ...s.tab, ...(activeTab === 'counter' ? s.tabActive : {}) }}>
          {T.tabCounter}
        </button>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: 16 }}>

        {/* 呼び出しタブ */}
        {activeTab === 'calls' && (
          <div>
            <div style={{ marginBottom: 12 }}>
              {!manualOrderMode ? (
                <button onClick={() => setManualOrderMode(true)}
                  style={{ width: '100%', padding: '12px', background: '#fff',
                    border: '2px dashed #667eea', borderRadius: 12, color: '#667eea',
                    fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                  {T.manualOrderBtn}
                </button>
              ) : (
                <div style={{ background: '#fff', borderRadius: 12, padding: 16,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '2px solid #667eea' }}>
                  <p style={{ margin: '0 0 10px', fontWeight: 600, color: '#2d3748' }}>{T.selectTable}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {tables.map(table => (
                      <button key={table.id} onClick={() => setSelectedTable(table)}
                        style={{ padding: '8px 16px', borderRadius: 8, border: '2px solid',
                          borderColor: selectedTable && selectedTable.id === table.id ? '#667eea' : '#e2e8f0',
                          background: selectedTable && selectedTable.id === table.id ? '#667eea' : '#f7fafc',
                          color: selectedTable && selectedTable.id === table.id ? '#fff' : '#2d3748',
                          fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                        {table.name}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setManualOrderMode(false); setSelectedTable(null); }}
                      style={{ flex: 1, padding: '10px', background: '#e2e8f0', border: 'none',
                        borderRadius: 8, color: '#4a5568', fontWeight: 600, cursor: 'pointer' }}>
                      {T.cancel}
                    </button>
                    <button onClick={() => { if (selectedTable) setOrderItems({}); }}
                      disabled={!selectedTable}
                      style={{ flex: 2, padding: '10px', border: 'none', borderRadius: 8,
                        background: selectedTable ? '#667eea' : '#e2e8f0',
                        color: selectedTable ? '#fff' : '#a0aec0',
                        fontWeight: 600, cursor: selectedTable ? 'pointer' : 'default' }}>
                      {T.orderInputArrow}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {pendingCalls.length === 0 ? (
              <div style={s.emptyBox}>
                <div style={{ display:"flex", justifyContent:"center" }}><CheckCircle size={40} color="#2d6a4f" /></div>
                <p style={{ color: '#718096', margin: '8px 0 0' }}>{T.noCalls}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pendingCalls.map(call => (
                  <div key={call.id} style={s.callCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                      <Bell size={28} color="#2d6a4f" />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 18, color: '#2d3748' }}>{call.tableName}</div>
                        <div style={{ fontSize: 12, color: '#a0aec0' }}>
                          {call.createdAt && call.createdAt.toDate
                            ? call.createdAt.toDate().toLocaleTimeString(dtLocale, { hour: '2-digit', minute: '2-digit' })
                            : ''}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button onClick={() => { setOrderingCall(call); setOrderItems({}); }}
                        style={{ ...s.btnPrimary, fontSize: 13 }}><UtensilsCrossed size={14} style={{display:'inline',verticalAlign:'middle',marginRight:4}} />{T.orderInputBtn}</button>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleConfirm(call.id)} style={s.btnConfirm}>{T.confirmBtn}</button>
                        <button onClick={() => handleDone(call.id)} style={s.btnDone}>{T.doneBtn}</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 注文一覧タブ */}
        {activeTab === 'orders' && (() => {
          const tableMap = {};
          orders.forEach(order => {
            const key = order.tableName;
            if (!tableMap[key]) tableMap[key] = { tableName: key, orders: [], total: 0 };
            tableMap[key].orders.push(order);
            tableMap[key].total += order.totalPrice || 0;
          });
          const tableList = Object.values(tableMap);
          return (
            <div>
              {tableList.length === 0 ? (
                <div style={s.emptyBox}>
                  <div style={{ display:"flex", justifyContent:"center" }}><UtensilsCrossed size={40} color="#2d6a4f" /></div>
                  <p style={{ color: '#718096', margin: '8px 0 0' }}>{T.noOrders}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {tableList.map(table => (
                    <div key={table.tableName} style={{ background: '#fff', borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden',
                      borderLeft: '4px solid #667eea' }}>
                      <div style={{ background: '#667eea', padding: '10px 16px' }}>
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>🪑 {table.tableName}</span>
                      </div>
                      <div style={{ padding: '8px 16px', background: '#fffbea', borderBottom: '1px solid #f0f0f0' }}>
                        <input
                          key={table.tableName + '-' + (table.orders[0]?.note || '')}
                          defaultValue={table.orders[0]?.note || ''}
                          onBlur={e => handleSaveTableNote(table, e.target.value)}
                          placeholder={T.notePlaceholder}
                          style={{ width: '100%', border: '1px solid #f6e05e', borderRadius: 6,
                            padding: '6px 8px', fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
                        />
                      </div>
                      {table.orders.map(order => (
                        <div key={order.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: '#a0aec0' }}>
                              {order.createdAt && order.createdAt.toDate
                                ? order.createdAt.toDate().toLocaleTimeString(dtLocale, { hour: '2-digit', minute: '2-digit' })
                                : ''}
                              <span style={{ marginLeft: 6, color: '#cbd5e0' }}>{T.thisOrderAmount((order.totalPrice||0).toLocaleString())}</span>
                            </span>
                            {(
                              <span style={{ display: 'flex', gap: 6 }}>
                                <button
                                  onClick={() => { if (window.confirm(T.confirmCancelOrder)) handleOrderCancel(order); }}
                                  style={{ padding: '3px 8px', background: '#fed7d7', color: '#c53030',
                                    border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                                  {T.cancelThisOrder}
                                </button>
                                <button
                                  onClick={() => handlePayOrders([order])}
                                  style={{ padding: '3px 8px', background: '#c6f6d5', color: '#276749',
                                    border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                                  {T.payThisOrder}
                                </button>
                              </span>
                            )}
                          </div>
                          {order.items.map((item, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center', padding: '6px 0',
                              borderBottom: '1px solid #f7fafc', gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: 14,
                                  color: item.status === 'served' ? '#a0aec0' : '#2d3748',
                                  textDecoration: item.status === 'served' ? 'line-through' : 'none' }}>
                                  {itemName(item)} × {item.quantity}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                                {(item.status === 'cooking' || !item.status) && (
                                  item.quantity > 1 ? (
                                    <button onClick={() => handleItemProgress(order.id, i, order)}
                                      style={{ padding: '4px 10px', background: '#ed8936', color: '#fff',
                                        border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                                      {T.cooked} ({item.readyCount || 0}/{item.quantity})
                                    </button>
                                  ) : (
                                    <button onClick={() => handleItemStatus(order.id, i, 'ready', order)}
                                      style={{ padding: '4px 10px', background: '#ed8936', color: '#fff',
                                        border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                                      {T.cooked}
                                    </button>
                                  )
                                )}
                                {item.status === 'ready' && (
                                  <>
                                    <button onClick={() => handleItemStatus(order.id, i, 'cooking', order)}
                                      style={{ padding: '4px 8px', background: '#e2e8f0', color: '#4a5568',
                                        border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>{T.back}</button>
                                    <button onClick={() => handleItemStatus(order.id, i, 'served', order)}
                                      style={{ padding: '4px 10px', background: '#667eea', color: '#fff',
                                        border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>{T.served}</button>
                                  </>
                                )}
                                {item.status === 'served' && (
                                  <>
                                    <button onClick={() => handleItemStatus(order.id, i, 'ready', order)}
                                      style={{ padding: '4px 8px', background: '#e2e8f0', color: '#4a5568',
                                        border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>{T.back}</button>
                                    <CheckCircle size={14} color="#68d391" />
                                  </>
                                )}
                                <span style={{ fontSize: 13, color: '#718096' }}>
                                  ¥{(item.price * item.quantity).toLocaleString()}
                                </span>
                                <button onClick={() => handleItemCancel(order, i)}
                                  style={{ background: "none", border: "none", cursor: "pointer",
                                    padding: "2px 4px", color: "#e53e3e", display: "flex", alignItems: "center" }}>
                                  <Trash2 size={15} />
                                </button>

                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                      <div style={{ padding: '12px 16px', display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center', background: '#f7fafc' }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#2d3748' }}>
                          <span>{T.total}</span>
                          <span style={{ fontSize: 11, color: '#718096', marginLeft: 4, fontWeight: 400 }}>{T.taxIncluded}</span>
                          <span style={{ marginLeft: 4 }}>¥{table.total.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => { if (window.confirm(T.confirmCancelTable)) Promise.all(table.orders.map(o => handleOrderCancel(o))); }}
                            style={{ background: '#fed7d7', color: '#c53030', border: 'none',
                              borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                            {T.cancel}
                          </button>
                          <button onClick={() => handlePayOrders(table.orders)}
                            style={{ ...s.btnDone, padding: '8px 20px' }}>
                            {T.payAndClear}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* 会計済みタブ */}
        {activeTab === 'paid' && (() => {
          const tableMap = {};
          paidOrders.forEach(order => {
            const key = order.tableName;
            if (!tableMap[key]) tableMap[key] = { tableName: key, orders: [], total: 0 };
            tableMap[key].orders.push(order);
            tableMap[key].total += order.totalPrice || 0;
          });
          const tableList = Object.values(tableMap).sort((a, b) => {
            const at = a.orders[0] && a.orders[0].paidAt && a.orders[0].paidAt.toDate ? a.orders[0].paidAt.toDate().getTime() : 0;
            const bt = b.orders[0] && b.orders[0].paidAt && b.orders[0].paidAt.toDate ? b.orders[0].paidAt.toDate().getTime() : 0;
            return bt - at;
          });
          return (
            <div>
              <p style={{ color: '#718096', fontSize: 13, marginBottom: 12 }}>
                {T.paidTabDesc}
              </p>
              {tableList.length === 0 ? (
                <div style={s.emptyBox}>
                  <div style={{ display:"flex", justifyContent:"center" }}><CheckCircle size={40} color="#2d6a4f" /></div>
                  <p style={{ color: '#718096', margin: '8px 0 0' }}>{T.noPaidToday}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {tableList.map(table => (
                    <div key={table.tableName} style={{ background: '#fff', borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                      <div style={{ padding: '10px 16px', background: '#edf2f7',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#2d3748' }}>{table.tableName}</span>
                        <span style={{ fontSize: 13, color: '#718096' }}>{T.totalWithAmount(table.total.toLocaleString())}</span>
                      </div>
                      {table.orders.map(order => (
                        <div key={order.id} style={{ padding: '10px 16px', borderTop: '1px solid #f0f0f0',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 12, color: '#a0aec0' }}>
                              {order.paidAt && order.paidAt.toDate
                                ? T.paidAtLabel + order.paidAt.toDate().toLocaleTimeString(dtLocale, { hour: '2-digit', minute: '2-digit' })
                                : ''}
                            </div>
                            <div style={{ fontSize: 13, color: '#4a5568' }}>
                              {(order.items || []).map(it => `${itemName(it)}×${it.quantity}`).join(lang === 'en' ? ', ' : '、')}
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#2d3748' }}>
                              ¥{(order.totalPrice || 0).toLocaleString()}
                            </div>
                          </div>
                          <button onClick={() => handleRevertToOpen(order)}
                            style={{ background: '#fffbea', color: '#b7791f', border: '1px solid #f6e05e',
                              borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
                            {T.revertToOpen}
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* カウントタブ */}
        {activeTab === 'counter' && (
          <div>
            <p style={{ color: '#718096', fontSize: 13, marginBottom: 12 }}>
              {T.counterDesc}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {menus.map(menu => (
                <div key={menu.id} style={{ ...s.menuCard, opacity: menu.status === 'soldout' ? 0.6 : 1 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#667eea' }}>No.{menu.menuNumber || '—'}</div>
                    <div style={{ fontWeight: 600, color: '#2d3748', fontSize: 15 }}>{menuName(menu)}</div>
                    <div style={{ fontSize: 13, color: '#718096' }}>
                      ¥{menu.price ? menu.price.toLocaleString() : 0} {T.soldToday(menu.soldCount || 0)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => handleToggleStatus(menu)} style={{
                      ...s.btnSoldout,
                      background: menu.status === 'soldout' ? '#fed7d7' : '#edf2f7',
                      color: menu.status === 'soldout' ? '#c53030' : '#4a5568',
                    }}>
                      {menu.status === 'soldout' ? T.soldOutActive : T.soldOutBtn}
                    </button>
                    <button onClick={() => handleSoldCount(menu, -1)}
                      disabled={menu.status === 'soldout' || (menu.soldCount || 0) === 0}
                      style={{ ...s.btnCount, background: '#e2e8f0', color: '#4a5568' }}>－1</button>
                    <button onClick={() => handleSoldCount(menu)}
                      disabled={menu.status === 'soldout'} style={s.btnCount}>＋1</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f7fafc', padding: 20 },
  pinBox: { background: '#fff', borderRadius: 16, padding: '32px 24px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)', textAlign: 'center', maxWidth: 320, width: '100%' },
  planBox: { background: '#fff', borderRadius: 16, padding: 32,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)', textAlign: 'center', maxWidth: 360 },
  logo: { width: 56, height: 56, borderRadius: 10, objectFit: 'cover', marginBottom: 12 },
  header: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '12px 20px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 },
  tabBar: { display: 'flex', background: '#fff', borderBottom: '1px solid #e2e8f0',
    position: 'sticky', top: 56, zIndex: 99 },
  tab: { flex: 1, padding: '14px 0', border: 'none', background: 'transparent',
    fontSize: 14, color: '#718096', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: 6, fontWeight: 500 },
  tabActive: { color: '#667eea', borderBottom: '2px solid #667eea' },
  badge: { background: '#e53e3e', color: '#fff', fontSize: 11, fontWeight: 700,
    borderRadius: 99, padding: '1px 6px', minWidth: 18, textAlign: 'center' },
  callCard: { background: '#fff', borderRadius: 12, padding: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex',
    alignItems: 'center', gap: 12, borderLeft: '4px solid #ed8936' },
  menuCard: { background: '#fff', borderRadius: 12, padding: '14px 16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12 },
  emptyBox: { background: '#fff', borderRadius: 12, padding: 40,
    textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  btnPrimary: { background: '#667eea', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 24px', fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  btnConfirm: { background: '#ebf4ff', color: '#3182ce', border: 'none', borderRadius: 8,
    padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnDone: { background: '#c6f6d5', color: '#276749', border: 'none', borderRadius: 8,
    padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnSoldout: { border: 'none', borderRadius: 8, padding: '6px 10px',
    fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  btnCount: { background: '#667eea', color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 16px', fontSize: 15, cursor: 'pointer', fontWeight: 700 },
  iconBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: 0 },
  logoutBtn: { background: 'rgba(255,255,255,0.15)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8,
    padding: '6px 14px', fontSize: 13, cursor: 'pointer' },
};
